import { Fragment, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cloneDeep, isEqual } from 'lodash';
import produce from 'immer';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  IWork,
  flowsQuery,
  flowSteps,
  PageLayout,
  PageNames,
  DisconnectedIllustration,
  getResponseErrorMessage,
  useEditorStore,
  FlowByIdVersionEnum,
  WorkLayout,
  ILayoutComponent,
} from '@diana/core';
import { useWorkPageLeftDrawerProps } from '../../../WorkPage/useWorkPageDrawerProps';
import { WorkPageTabsEnum } from '../../../WorkPage/WorkPage.constants';
import { PreviewViewNavbar } from './PreviewViewNavbar';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';

export const PreviewView = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette } = useTheme();

  const { isLoading: isPageLoading, error: pageError } = useFlowBuilder2PageContext();

  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const data = useEditorStore((s) => s.getFlowDefaultData(), isEqual);

  const { key: getFlowKey, fn: getFlowFn } = flowsQuery.getById({
    id: flowId,
    version: FlowByIdVersionEnum.Specific,
  });
  const { data: flow, isLoading: isLoadingFlow } = useQuery({
    queryKey: getFlowKey,
    queryFn: getFlowFn,
    retryOnMount: true,
    enabled: !!flowId,
  });

  const { key: getStepsKey, fn: getStepsFn } = flowSteps.getById({ id: flowId });
  const { data: steps, isLoading: isLoadingSteps } = useQuery({
    queryKey: getStepsKey,
    queryFn: getStepsFn,
    retryOnMount: true,
    enabled: !!flowId,
  });

  const work = useMemo(() => {
    if (flow) {
      return cloneDeep(
        produce(flow as unknown as IWork, (draft) => {
          draft.steps = cloneDeep(steps ?? []);
          for (const step of draft.steps) {
            step.displayName = step.displayName || t('labels.untitledStep');
            step.userPermissions = ['PreviewMode'];
            for (const task of step.steps) {
              task.displayName = task.displayName || t('labels.untitledTask');
              task.userPermissions = ['PreviewMode'];
              task.initiatorPartyName = 'Owner';
              (function scan(items: ILayoutComponent[]) {
                for (const item of items) {
                  item.name ??= '';
                  if (Array.isArray(item.components)) {
                    scan(item.components);
                  }
                }
              })(task.layout?.components ?? []);
            }
          }
        }),
      );
    }
    return undefined;
  }, [t, flow, steps]);

  const leftDrawer = useWorkPageLeftDrawerProps({
    work: work,
    flow: flow,
    isLoading: isPageLoading || isLoadingFlow || isLoadingSteps,
    tabs: [WorkPageTabsEnum.Workflow],
  });

  return (
    <Fragment>
      <PreviewViewNavbar />
      <PageLayout
        name={PageNames.FLOW_BUILDER}
        leftDrawer={leftDrawer}
        backgroundColor={palette.background.paper}
      >
        {pageError ? (
          <Stack alignItems="center" spacing={2} p={5}>
            <DisconnectedIllustration />
            <Typography>{getResponseErrorMessage(pageError)}</Typography>
          </Stack>
        ) : (
          <Box sx={{ '.CollapsiblePanel-sticky-header': { top: 0 } }}>
            {work ? (
              <WorkLayout
                work={work}
                data={data}
                transformStep={({ step }) => ({ ...step, isActive: true })}
                renderTaskHeader={({ step }) => (
                  <Typography sx={{ fontSize: 16 }}>{step.displayName}</Typography>
                )}
              />
            ) : null}
          </Box>
        )}
      </PageLayout>
    </Fragment>
  );
};
